'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Search, ArrowLeftRight, ChevronDown } from 'lucide-react';
import SmartLogo from '@/components/tool/SmartLogo';
import { comparePairHref } from '@/lib/links';
import type { Locale } from '@/lib/i18n';

interface CompareTool {
  slug: string;
  name: string;
  logo_url: string | null;
  website_url: string | null;
  profession_slug: string;
}

interface CompareSelectorProps {
  allTools: CompareTool[];
  currentLeftSlug: string;
  currentRightSlug: string;
  locale: Locale;
}

function ToolDropdown({
  label,
  selectedSlug,
  tools,
  onSelect,
  side,
}: {
  label: string;
  selectedSlug: string;
  tools: CompareTool[];
  onSelect: (slug: string) => void;
  side: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return tools;
    const q = query.toLowerCase();
    return tools.filter((t) => t.name.toLowerCase().includes(q));
  }, [query, tools]);

  const selected = tools.find((t) => t.slug === selectedSlug);

  return (
    <div ref={ref} className="relative flex-1 min-w-0">
      <label className="block text-[0.625rem] font-black uppercase tracking-[0.2em] text-muted-foreground mb-2">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 rounded-2xl border border-border/60 bg-card/70 backdrop-blur-sm px-4 py-3 text-left transition-all hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Select ${label} tool`}
      >
        {selected && (
          <div className="h-8 w-8 shrink-0 overflow-hidden rounded-lg border border-border/40 bg-muted/40 p-0.5 relative">
            <SmartLogo
              logoUrl={selected.logo_url}
              websiteUrl={selected.website_url}
              name={selected.name}
              size={32}
              className="object-contain rounded-md"
            />
          </div>
        )}
        <span className="flex-1 truncate text-sm font-semibold text-foreground">
          {selected?.name || 'Select a tool…'}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="absolute z-50 mt-2 w-full rounded-2xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-2xl overflow-hidden"
          role="listbox"
          aria-label={`${label} tool options`}
        >
          <div className="p-3 border-b border-border/40">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tools…"
                className="w-full rounded-xl border border-border/40 bg-muted/30 py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary/40 focus:ring-1 focus:ring-ring/30 transition-colors"
                aria-label="Search tools"
              />
            </div>
          </div>
          <ul className="max-h-64 overflow-y-auto p-1.5">
            {filtered.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-muted-foreground">
                No tools found
              </li>
            )}
            {filtered.map((tool) => (
              <li key={tool.slug}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(tool.slug);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-all hover:bg-primary/10 ${
                    tool.slug === selectedSlug
                      ? 'bg-primary/10 font-semibold text-primary'
                      : 'text-foreground'
                  }`}
                  role="option"
                  aria-selected={tool.slug === selectedSlug}
                >
                  <div className="h-7 w-7 shrink-0 overflow-hidden rounded-lg border border-border/30 bg-muted/30 p-0.5 relative">
                    <SmartLogo
                      logoUrl={tool.logo_url}
                      websiteUrl={tool.website_url}
                      name={tool.name}
                      size={28}
                      className="object-contain rounded-md"
                    />
                  </div>
                  <span className="truncate">{tool.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CompareSelector({
  allTools,
  currentLeftSlug,
  currentRightSlug,
  locale,
}: CompareSelectorProps) {
  const router = useRouter();
  const [leftSlug, setLeftSlug] = useState(currentLeftSlug);
  const [rightSlug, setRightSlug] = useState(currentRightSlug);

  const handleSwap = () => {
    const newLeft = rightSlug;
    const newRight = leftSlug;
    setLeftSlug(newLeft);
    setRightSlug(newRight);
    router.push(comparePairHref(locale, newLeft, newRight));
  };

  const handleLeftSelect = (slug: string) => {
    if (slug === rightSlug) return;
    setLeftSlug(slug);
    if (slug !== 'select' && rightSlug && rightSlug !== 'select') {
      router.push(comparePairHref(locale, slug, rightSlug));
    }
  };

  const handleRightSelect = (slug: string) => {
    if (slug === leftSlug) return;
    setRightSlug(slug);
    if (leftSlug && leftSlug !== 'select' && slug !== 'select') {
      router.push(comparePairHref(locale, leftSlug, slug));
    }
  };

  const canCompare = leftSlug && rightSlug && leftSlug !== 'select' && rightSlug !== 'select';

  return (
    <div className="glass-card rounded-3xl p-5">
      <h2 className="text-[0.625rem] font-black uppercase tracking-[0.2em] text-primary mb-5 flex items-center gap-2">
        <ArrowLeftRight className="h-3.5 w-3.5" aria-hidden="true" />
        Compare Options
      </h2>
      <div className="flex flex-col gap-4 relative">
        <ToolDropdown
          label="Tool A"
          selectedSlug={leftSlug}
          tools={allTools}
          onSelect={handleLeftSelect}
          side="left"
        />
        <div className="flex justify-center -my-2 relative z-10">
          <button
            type="button"
            onClick={handleSwap}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border/60 bg-card/90 backdrop-blur-sm text-primary transition-all hover:bg-primary hover:text-primary-foreground hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 shadow-sm"
            aria-label="Swap compared tools"
          >
            <ArrowLeftRight className="h-3.5 w-3.5 rotate-90" aria-hidden="true" />
          </button>
        </div>
        <ToolDropdown
          label="Tool B"
          selectedSlug={rightSlug}
          tools={allTools}
          onSelect={handleRightSelect}
          side="right"
        />
        
        <button
          type="button"
          disabled={!canCompare}
          onClick={() => {
            if (canCompare) {
              router.push(comparePairHref(locale, leftSlug, rightSlug));
            }
          }}
          className="mt-2 w-full flex items-center justify-center rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Compare Tools
        </button>
      </div>
    </div>
  );
}
